import { ASSETS, BRAND, NAV_IDS } from "../lib/data";
import { Instagram, Facebook } from "lucide-react";
import { useT } from "../lib/i18n";

const TikTokIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export const Footer = () => {
  const { t, lang } = useT();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-gold/15 pt-20 md:pt-32 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col items-center text-center">
          <img
            src={ASSETS.logoGold}
            alt="Monsieur Toast"
            data-testid="footer-logo-large"
            className="h-64 sm:h-80 md:h-[28rem] lg:h-[32rem] w-auto select-none -my-12 md:-my-20"
            draggable="false"
          />
          <p className="mt-6 text-[10px] md:text-xs tracking-[0.45em] uppercase text-gold/80">
            {t("footer.tagline")}
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 border-t border-gold/15 pt-10">
          <div className="flex items-center gap-2">
            <img
              src={ASSETS.logoGold}
              alt="Monsieur Toast"
              className="h-32 md:h-40 w-auto -my-8"
            />
            <div className="text-white/50 text-sm font-light max-w-xs">
              {t("footer.about")}
            </div>
          </div>

          <div className="md:justify-self-center">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold/70 mb-4">
              {t("footer.sections")}
            </div>
            <div className="flex flex-col gap-2">
              {NAV_IDS.map((id) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(id)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid={`footer-nav-${id}`}
                  className="text-sm font-light text-white/70 hover:text-gold transition-colors"
                >
                  {t(`nav.${id}`)}
                </a>
              ))}
            </div>
          </div>

          <div className="md:justify-self-end">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold/70 mb-4">
              {t("footer.contact")}
            </div>
            <div className="flex flex-col gap-2 text-sm font-light">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="text-white/70 hover:text-gold transition-colors"
              >
                {BRAND.email}
              </a>
              <a
                href={BRAND.phoneHref}
                data-testid="footer-phone"
                className="text-white/70 hover:text-gold transition-colors"
              >
                {BRAND.phone}
              </a>
              <a
                href={BRAND.phoneCHHref}
                data-testid="footer-phone-ch"
                className="text-white/60 hover:text-gold transition-colors"
              >
                {BRAND.phoneCH}
              </a>
              <span className="text-white/50">{BRAND.address[lang]}</span>
            </div>
          </div>
        </div>

        {/* Social row */}
        <div className="mt-14 flex flex-col items-center gap-6 border-t border-gold/15 pt-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/70">
            {t("footer.follow")}
          </div>
          <div className="flex items-center gap-8 md:gap-10">
            <a
              data-testid="footer-social-instagram"
              href={BRAND.social.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="group flex items-center gap-3 text-white/70 hover:text-gold transition-colors"
            >
              <Instagram size={20} strokeWidth={1.4} />
              <span className="hidden sm:inline text-sm font-light tracking-wide">
                {BRAND.social.instagram.handle}
              </span>
            </a>
            <a
              data-testid="footer-social-tiktok"
              href={BRAND.social.tiktok.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="group flex items-center gap-3 text-white/70 hover:text-gold transition-colors"
            >
              <TikTokIcon size={20} />
              <span className="hidden sm:inline text-sm font-light tracking-wide">
                {BRAND.social.tiktok.handle}
              </span>
            </a>
            <a
              data-testid="footer-social-facebook"
              href={BRAND.social.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="group flex items-center gap-3 text-white/70 hover:text-gold transition-colors"
            >
              <Facebook size={20} strokeWidth={1.4} />
              <span className="hidden sm:inline text-sm font-light tracking-wide">
                {BRAND.social.facebook.handle}
              </span>
            </a>
          </div>
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 border-t border-gold/10 pt-6 text-[11px] text-white/40 font-light">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} {BRAND.name}. {t("footer.rights")}
          </div>
          <div className="tracking-[0.35em] uppercase text-gold/60">
            {t("footer.slogan")}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
